import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, FileText, Loader2, MapPin, Users, CalendarDays, XCircle, ArrowRight, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtKr = (n: number) => `kr ${Math.round(n).toLocaleString('nb-NO')},-`;
const fmtDate = (d?: string | null) => {
  if (!d) return null;
  try { return new Date(d + 'T00:00:00').toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

export default function OfferAcceptPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [offer, setOffer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [reaccepting, setReaccepting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/offers/${token}`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setOffer(data);
        setName(data.customer_name || '');
        // Fase 2: hvis tilbudet er godtatt MEN har en ventende endring, viser vi
        // «Godta endringene» i stedet for «allerede godtatt».
        if (data.status === 'accepted' && !data.pending_revision) {
          setResult({ already_accepted: true, booking_number: data.booking_number });
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/public/offers/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted_by_name: name || null, comment: comment || null }),
      });
      if (!res.ok) throw new Error();
      setResult(await res.json());
    } catch {
      setError('Noe gikk galt. Prøv igjen, eller kontakt oss direkte.');
    } finally {
      setAccepting(false);
    }
  };

  const handleReaccept = async () => {
    setReaccepting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/public/offers/${token}/reaccept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted_by_name: name || null, comment: comment || null }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResult({ ...data, reaccepted: true });
    } catch {
      setError('Noe gikk galt. Prøv igjen, eller kontakt oss direkte.');
    } finally {
      setReaccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" data-testid="offer-accept-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center" data-testid="offer-accept-not-found">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">{t('offerAcceptPage.tilbudetBleIkkeFunnet', 'Tilbudet ble ikke funnet')}</h1>
          <p className="text-sm text-slate-500">{t('offerAcceptPage.lenkenKanVaereUtloptEller', 'Lenken kan være utløpt eller ugyldig. Kontakt oss hvis du mener dette er en feil.')}</p>
        </div>
      </div>
    );
  }

  const dateTxt = fmtDate(offer.outbound_date);

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{offer.company_name}</p>
          <h1 className="text-2xl font-bold text-slate-800 mt-1">{t('offerAcceptPage.tilbud', { defaultValue: 'Tilbud {{v1}}', v1: offer.offer_id })}</h1>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="offer-accept-card">
          <div className="p-6 space-y-3 border-b border-slate-100">
            {offer.round_trip ? (
              <div className="space-y-3" data-testid="offer-roundtrip-legs">
                <div className="flex items-start gap-3 text-slate-700">
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('offerAcceptPage.utreise', 'Utreise')}</div>
                    <div data-testid="offer-leg-outbound">{offer.round_trip.outbound.from} → {offer.round_trip.outbound.to}{offer.round_trip.outbound.date ? ` · ${fmtDate(offer.round_trip.outbound.date)}` : ''}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-slate-700">
                  <ArrowLeftRight className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('offerAcceptPage.retur', 'Retur')}</div>
                    <div data-testid="offer-leg-return">{offer.round_trip.return.from} → {offer.round_trip.return.to}{offer.round_trip.return.date ? ` · ${fmtDate(offer.round_trip.return.date)}` : ''}</div>
                  </div>
                </div>
                {offer.group_size && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{t('offerAcceptPage.passasjerer', { defaultValue: '{{v1}} passasjerer', v1: offer.group_size })}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
                {(offer.from_location || offer.to_location) && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span data-testid="offer-route">{offer.from_location}{offer.to_location ? ` → ${offer.to_location}` : ''}</span>
                  </div>
                )}
                {dateTxt && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{dateTxt}{offer.return_date && offer.return_date !== offer.outbound_date ? ` – ${fmtDate(offer.return_date)}` : ''}</span>
                  </div>
                )}
                {offer.group_size && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{t('offerAcceptPage.passasjerer', { defaultValue: '{{v1}} passasjerer', v1: offer.group_size })}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-6 bg-slate-50 border-b border-slate-100">
            {offer.alternatives?.length > 1 ? (
              <div className="space-y-2">
                {offer.alternatives.map((a: any, i: number) => (
                  <div key={i} className="flex justify-between items-baseline">
                    <span className="text-sm text-slate-600">{a.label}</span>
                    <span className="font-bold text-slate-800">{fmtKr(a.total_price)}</span>
                  </div>
                ))}
              </div>
            ) : offer.total_price ? (
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-600">{t('offerAcceptPage.totalpris', 'Totalpris')}</span>
                <span className="text-2xl font-bold text-slate-800" data-testid="offer-total-price">{fmtKr(offer.total_price)}</span>
              </div>
            ) : null}
            <p className="text-xs text-slate-400 mt-2">{offer.vat_included ? t('offerAccept.allPricesInclVat', 'Alle priser er inkl. mva.') : t('offerAccept.allPricesExclVat', 'Alle priser er ekskl. mva.')}</p>
          </div>

          <div className="p-6">
            {result ? (
              <div className="text-center py-4" data-testid="offer-accept-success">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-slate-800">
                  {result.reaccepted ? 'Takk! Endringene er godtatt'
                    : result.already_accepted ? t('offerAcceptPage.tilbudetErAlleredeGodtatt', 'Tilbudet er allerede godtatt')
                    : 'Takk! Tilbudet er godtatt'}
                </h2>
                {result.booking_number && (
                  <p className="text-sm text-slate-600 mt-2">
                    Bookingnummer: <span className="font-mono font-bold" data-testid="offer-booking-number">{result.booking_number}</span>
                    {(result.is_round_trip || offer.round_trip) && (
                      <span className="ml-1 text-indigo-600" data-testid="offer-roundtrip-confirmed"> {t('offerAcceptPage.turRetur2Etapper', '· tur/retur (2 etapper)')}</span>
                    )}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-3">{t('offerAcceptPage.viTarKontaktForA', 'Vi tar kontakt for å bekrefte detaljene.')}</p>
              </div>
            ) : offer.pending_revision ? (
              <div className="space-y-3" data-testid="offer-reaccept-block">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="offer-revision-banner">
                  <p className="text-sm font-semibold text-amber-800">{t('offerAcceptPage.tilbudetErOppdatert', 'Tilbudet er oppdatert')}</p>
                  <p className="text-xs text-amber-700 mt-0.5">{t('offerAcceptPage.detFinnesNyeVilkarFor', 'Det finnes nye vilkår for denne bookingen. Se detaljene over og godta endringene for å bekrefte.')}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t('offerAcceptPage.dittNavn', 'Ditt navn')}</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('offerAcceptPage.navn', 'Navn')} data-testid="offer-reaccept-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t('offerAcceptPage.kommentarValgfritt', 'Kommentar (valgfritt)')}</label>
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('offerAcceptPage.fEksOnsketHentetidspunkt', 'F.eks. ønsket hentetidspunkt')} data-testid="offer-reaccept-comment-input" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="offer-reaccept-error">{error}</p>}
                <Button
                  onClick={handleReaccept}
                  disabled={reaccepting}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-6 text-base"
                  data-testid="offer-reaccept-button"
                >
                  {reaccepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowLeftRight className="w-4 h-4 mr-2" />}
                  {t('offerAcceptPage.godtaEndringene', 'Godta endringene')}
                </Button>
                <p className="text-[11px] text-slate-400 text-center">
                  {t('offerAcceptPage.vedAGodtaBekrefterDuOppdaterteVilkar', { defaultValue: 'Ved å godta bekrefter du de oppdaterte vilkårene fra {{company}}. Bookingen din oppdateres automatisk.', company: offer.company_name })}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">{t('offerAcceptPage.dittNavn', 'Ditt navn')}</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('offerAcceptPage.navn', 'Navn')} data-testid="offer-accept-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t('offerAcceptPage.kommentarValgfritt', 'Kommentar (valgfritt)')}</label>
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('offerAcceptPage.fEksOnsketHentetidspunkt', 'F.eks. ønsket hentetidspunkt')} data-testid="offer-accept-comment-input" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="offer-accept-error">{error}</p>}
                <Button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-base"
                  data-testid="offer-accept-button"
                >
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  {t('offerAcceptPage.godtaTilbudet', 'Godta tilbudet')}
                </Button>
                <p className="text-[11px] text-slate-400 text-center">
                  {t('offerAcceptPage.vedAGodtaBekrefterDuTilbudet', { defaultValue: 'Ved å godta bekrefter du tilbudet fra {{company}}. En booking opprettes automatisk og vi kontakter deg for endelig bekreftelse.', company: offer.company_name })}
                </p>
              </div>
            )}
          </div>
        </div>

        {(offer.company_phone || offer.company_email) && (
          <p className="text-center text-xs text-slate-400 mt-4">
            Spørsmål? {offer.company_phone && `Ring ${offer.company_phone}`}{offer.company_phone && offer.company_email && ' eller '}
            {offer.company_email && `send e-post til ${offer.company_email}`}
          </p>
        )}
      </div>
    </div>
  );
}
